import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiVerifyEmail, type VerifyEmailOutcome } from '../../app/auth/authApi';
import { LiftXLogo } from '../../ui/components/LiftXLogo';
import { ROUTES } from '../../ui/navigation/routes';
// Same full-screen auth-card shape as Login/Register — reuses their CSS
// module rather than a near-duplicate file, exactly as RegisterScreen does.
import styles from './LoginScreen.module.css';

/**
 * The `/verify-email?token=…` landing page (email-verification addendum).
 *
 * Reads the token from the query string and POSTs it to
 * `/api/auth/verify-email`. The token is never persisted, never written to
 * IndexedDB, and never logged — it lives only in this component's closure
 * for the single request that redeems it.
 *
 * §16 (cross-account isolation): this screen has no idea which account it is
 * verifying, deliberately. The server resolves the token to a user id
 * entirely on its own side; nothing here reads `localDataOwner`,
 * `lastVerified`, or any other local ownership signal, so a stale/other
 * account's local state on this device cannot influence which server
 * account gets verified.
 */
export function VerifyEmailScreen(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [outcome, setOutcome] = useState<VerifyEmailOutcome | null>(null);

  /**
   * React 18+ StrictMode mounts effects twice in development. Redeeming a
   * SINGLE-USE token twice would make the second call legitimately report
   * "already used" and show the user a failure for a verification that
   * actually succeeded — so the request is fired at most once per mount,
   * guarded by a ref rather than by effect-dependency luck.
   */
  const requested = useRef(false);

  useEffect(() => {
    if (token === null || token.length === 0) {
      setOutcome('invalid');
      return;
    }
    if (requested.current) return;
    requested.current = true;

    void (async () => {
      setOutcome(await apiVerifyEmail(token));
    })();
  }, [token]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logoWrap}>
          <LiftXLogo height={96} />
        </div>

        {outcome === null && (
          <>
            <h1 className={styles.title}>Verifying…</h1>
            <p className={styles.subtitle} role="status">
              Confirming your email address.
            </p>
          </>
        )}

        {outcome === 'verified' && (
          <>
            <h1 className={styles.title}>Email verified</h1>
            <p className={styles.subtitle} role="status">
              Thank you — your email address has been verified. Your account is now awaiting
              administrator approval, and you&apos;ll be able to sign in once it has been approved.
            </p>
          </>
        )}

        {(outcome === 'expired' || outcome === 'used' || outcome === 'invalid') && (
          <>
            <h1 className={styles.title}>Verification failed</h1>
            <p className={styles.subtitle} role="alert">
              {outcome === 'expired' && 'This verification link has expired. '}
              {outcome === 'used' && 'This verification link has already been used. '}
              {outcome === 'invalid' && 'This verification link is invalid. '}
              You can request a new one from the sign-in screen.
            </p>
          </>
        )}

        {outcome === 'network-error' && (
          <>
            <h1 className={styles.title}>Verification failed</h1>
            <p className={styles.subtitle} role="alert">
              Can&apos;t reach the server. Check your connection and open the link again.
            </p>
          </>
        )}

        <Link to={ROUTES.dashboard} className={styles.submit}>
          Go to Sign in
        </Link>
      </div>
    </div>
  );
}
